/** Pequenas animações do quadro (respeitam prefers-reduced-motion). */
export const EASE = 'cubic-bezier(.2,.8,.2,1)';
export const SPRING = 'cubic-bezier(.34,1.56,.64,1)';
export const motionOk = () => !matchMedia('(prefers-reduced-motion: reduce)').matches && !!document.body.animate;

/** Faíscas ao finalizar uma tarefa. */
export function celebrate(taskId: string) {
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>(`.kcard[data-card="${taskId}"]`);
    if (!el || !motionOk()) return;
    const r = el.getBoundingClientRect(), box = document.createElement('div');
    box.className = 'faisca';
    box.style.left = r.left + r.width / 2 + 'px';
    box.style.top = r.top + r.height / 2 + 'px';
    document.body.appendChild(box);
    const colors = ['var(--accent)', 'var(--label)', 'var(--accent)', '#C29A1E', 'var(--green)'];
    for (let i = 0; i < 12; i++) {
      const d = document.createElement('i');
      d.style.background = colors[i % colors.length];
      box.appendChild(d);
      const a = (i / 12) * Math.PI * 2 + Math.random() * 0.4, dist = 34 + Math.random() * 30;
      d.animate([
        { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
        { transform: `translate(calc(-50% + ${Math.cos(a) * dist}px), calc(-50% + ${Math.sin(a) * dist}px)) scale(.2)`, opacity: 0 },
      ], { duration: 650 + Math.random() * 250, easing: EASE, fill: 'forwards' });
    }
    el.animate([{ boxShadow: '0 0 0 0 transparent' }, { boxShadow: '0 0 0 5px color-mix(in srgb, var(--green) 28%, transparent)' }, { boxShadow: '0 0 0 0 transparent' }], { duration: 900, easing: EASE });
    setTimeout(() => box.remove(), 1000);
  }, 260);
}

/**
 * Centro visual (como está, com animação em curso) e centro de layout (sem a animação) de um cartão, em
 * coordenadas do conteúdo rolável: rolar a tela (ou o navegador ajustar a rolagem) não conta como mudar de lugar.
 */
export interface CardPos { vx: number; vy: number; lx: number; ly: number }
const scrollerOf = (root: HTMLElement) => root.closest<HTMLElement>('.qmain') ?? document.scrollingElement as HTMLElement;
function pos(el: HTMLElement, sc: HTMLElement | null): CardPos {
  const r = el.getBoundingClientRect();
  const vx = r.left + r.width / 2 + (sc?.scrollLeft ?? 0), vy = r.top + r.height / 2 + (sc?.scrollTop ?? 0);
  // O deslocamento da animação/transição em curso (translate) não é mudança de lugar: tira para achar o layout.
  const t = getComputedStyle(el).transform;
  const m = t && t !== 'none' ? new DOMMatrixReadOnly(t) : null;
  return { vx, vy, lx: vx - (m?.m41 ?? 0), ly: vy - (m?.m42 ?? 0) };
}

/**
 * Posições de todos os cartões AGORA. Tiradas durante a renderização, antes de o React aplicar a mudança, para
 * que rolagem e outras mudanças entre renders não contem como movimento.
 */
export function rects(root: HTMLElement | null) {
  const m = new Map<string, CardPos>();
  if (!root) return m;
  const sc = scrollerOf(root);
  root.querySelectorAll<HTMLElement>('.kcard').forEach(el => m.set(el.dataset.card!, pos(el, sc)));
  return m;
}

const FLIP_ID = 'quadro-flip';

/**
 * Depois da mudança aplicada: cada cartão que mudou de lugar desliza de onde estava (FLIP); cartão novo
 * aparece com um "pop" e destaque; na primeira exibição do quadro, os cartões entram em cascata.
 * Cartão que não mudou de lugar não é tocado (a animação que estiver rodando continua).
 */
export function flip(root: HTMLElement | null, prev: Map<string, CardPos> | null, opts: { entrance: boolean }) {
  if (!root || !motionOk()) return;
  const sc = scrollerOf(root);
  [...root.querySelectorAll<HTMLElement>('.kcard')].forEach((el, i) => {
    const p0 = prev?.get(el.dataset.card!);
    if (p0) {
      const p1 = pos(el, sc);
      if (Math.abs(p1.lx - p0.lx) + Math.abs(p1.ly - p0.ly) < 2) return;
      // Mudou de lugar: parte de onde estava visualmente (mesmo no meio de outra animação).
      el.getAnimations().forEach(a => { if (a.id === FLIP_ID) a.cancel(); });
      const dx = p0.vx - p1.lx, dy = p0.vy - p1.ly;
      const far = Math.abs(dx) > 40;
      const a = el.animate(far
        ? [{ transform: `translate(${dx}px,${dy}px) scale(1.03)`, boxShadow: '0 18px 40px -12px rgba(0,0,0,.3)' }, { transform: 'none', boxShadow: 'none' }]
        : [{ transform: `translate(${dx}px,${dy}px)` }, { transform: 'none' }],
      { duration: Math.min(560, 280 + Math.hypot(dx, dy) * 0.22), easing: far ? SPRING : EASE });
      a.id = FLIP_ID;
    } else if (opts.entrance) {
      const a = el.animate([{ opacity: 0, transform: 'translateY(10px) scale(.98)' }, { opacity: 1, transform: 'none' }],
        { duration: 400, delay: Math.min(i * 18, 420), easing: EASE, fill: 'backwards' });
      a.id = FLIP_ID;
    } else if (prev?.size) {
      const a = el.animate([{ opacity: 0, transform: 'scale(.9)' }, { opacity: 1, transform: 'none' }], { duration: 420, easing: SPRING });
      a.id = FLIP_ID;
      el.classList.add('novo');
      setTimeout(() => el.classList.remove('novo'), 1800);
    }
  });
}

/** Contagem da coluna que mudou dá um pulinho; a barra de limite de "Fazendo" cresce até o novo valor. */
export function bumpCounts(root: HTMLElement | null, prev: Map<string, string>, prevWip: string | null, entrance: boolean) {
  if (!root || !motionOk()) return;
  root.querySelectorAll<HTMLElement>('.col').forEach(c => {
    const now = c.querySelector('.col-count')?.textContent ?? '';
    const before = prev.get(c.dataset.col!);
    if (before !== undefined && before !== now) {
      c.querySelector('.col-count')?.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.4)' }, { transform: 'scale(1)' }], { duration: 420, easing: SPRING });
    }
  });
  const wi = root.querySelector<HTMLElement>('.wip-bar i');
  if (wi && ((prevWip && prevWip !== wi.style.width) || entrance)) {
    wi.animate([{ width: entrance ? '0%' : prevWip! }, { width: wi.style.width }], { duration: 700, easing: EASE });
  }
}
export const counts = (root: HTMLElement | null) => {
  const m = new Map<string, string>();
  root?.querySelectorAll<HTMLElement>('.col').forEach(c => m.set(c.dataset.col!, c.querySelector('.col-count')?.textContent ?? ''));
  return m;
};
